"use client";
import { useEffect, useState, useRef } from "react";
import dynamic from "next/dynamic";
import { useSearchParams, useRouter } from "next/navigation";
import {
  Box,
  InputBase,
  Stack,
  MenuItem,
  TextField,
  InputAdornment,
  Pagination
} from "@mui/material";
import {
  SwapVertRounded as SortIcon,
  ArrowForwardIosRounded as NextIcon,
  ArrowBackIosRounded as PreviousIcon
} from "@mui/icons-material";
import TaskIcon from "@mui-symbols-material/w400/FilterListRounded";

import { useStore, observer } from "../../service/mobx";
import { ModelCardSize25 as ModelCard } from "../../component/Card/Model";
import { modelSort } from "../../component/Menu/menus/Search/Sort";
import ButtonOutlined from "../../component/Button/Outlined";
import ButtonText from "../../component/Button/Text";
import { NextLink } from "../../component/Link";

const Tasks = dynamic(() => import("./Tasks"), { ssr: false });
const Masonry = dynamic(
  () => import("masonic").then(module => module.Masonry),
  { ssr: false }
);

function Models({
  transition,
  models,
  task,
  sort,
  count,
  page,
  categories,
  pageLimit
}) {
  const [width, setWidth] = useState(0);
  const { device } = useStore();
  const ref = useRef();
  const searchParams = new URLSearchParams(useSearchParams());
  const hasModel = searchParams.has("modelId");

  useEffect(() => {
    // used to keep masonry the right width during resize
    if (device.measured && device.isNotPhone && ref.current) {
      let timeoutId;
      const observer = new ResizeObserver(([entry]) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
          timeoutId = undefined;
          setWidth(Math.round(entry.contentRect.width / 100) * 100);
        }, 100);
      });

      observer.observe(ref.current);

      return () => {
        observer.disconnect();
        clearTimeout(timeoutId);
      };
    }
  }, [device.measured, device.isNotPhone]);

  return (
    <Stack
      ref={ref}
      spacing={3}
      sx={{
        transition,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        p: { compact: 0, expanded: 2 },
        borderRadius: hasModel ? 0 : "var(--shape-lg-end)",
        bgcolor: {
          compact: "transparent",
          expanded: "var(--surface-container-low)"
        },
        "@media (min-width: 840px)": {
          "&:hover": {
            bgcolor: "var(--surface-container-color)"
          },
          "&:hover .search": {
            bgcolor: "var(--surface-container-highest)"
          }
        }
      }}
    >
      <SearchAndFilter
        sort={sort}
        count={count}
        task={task}
        categories={categories}
      />
      <Masonry
        key={width}
        items={models}
        render={render}
        itemKey={itemKey}
        columnGutter={8}
        columnWidth={320}
        maxColumnCount={2}
        overscanBy={Infinity}
      />
      <ChangePage count={count} page={page} pageLimit={pageLimit} />
    </Stack>
  );
}

export default observer(Models);
const itemKey = model => model.id;
const render = ({ data: model }) => (
  <ModelCard noScroll href={model.href} model={model} />
);

function SearchAndFilter({ sort, count, task, categories }) {
  const searchParams = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("search"));
  const router = useRouter();

  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);

    if (newSearchParams.get("search") !== query) {
      const id = setTimeout(() => {
        if (query) {
          newSearchParams.set("search", query);
        } else if (newSearchParams.has("search")) {
          newSearchParams.delete("search");
        }

        newSearchParams.delete("page");

        router.push(`?${newSearchParams.toString()}`);
      }, 750);

      return () => clearTimeout(id);
    }
  }, [router, searchParams, query]);

  return (
    <Stack
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
      height={{ compact: "auto", large: 48 }}
      direction={{ compact: "column", large: "row" }}
    >
      <Box
        p={1}
        pt={1.5}
        px={2}
        width="100%"
        className="search"
        boxShadow="var(--elevation-1)"
        borderRadius="var(--shape-round)"
        bgcolor={{
          compact: "var(--surface-container-lowest)",
          expanded: "var(--surface-container-high)"
        }}
        sx={{
          transition: "all 300ms var(--motion-easing-emphasized)",
          "&:hover": {
            boxShadow: "var(--elevation-2)",
            bgcolor: {
              compact: "var(--surface-container-low)",
              expanded: "var(--surface-container-highest)"
            }
          }
        }}
      >
        <InputBase
          fullWidth
          size="small"
          value={query || ""}
          placeholder={`Search ${count.toLocaleString()} models`}
          sx={{
            position: "relative",
            typography: "titleMd",
            color: "var(--surface-on-color)",
            ".Mui-disabled": {
              fontStyle: "italic",
              WebkitTextFillColor: "var(--secondary-on-container)"
            }
          }}
          inputProps={{ sx: { color: "var(--surface-on-color)" } }}
          onChange={event => setQuery(event.target.value)}
        />
      </Box>
      <Filters task={task} categories={categories} sort={sort} />
    </Stack>
  );
}

const ChangePage = observer(function ChangePage({ page, count, pageLimit }) {
  const { device } = useStore();
  const searchParams = useSearchParams();
  const router = useRouter();
  const lastPage = Math.max(1, Math.min(Math.ceil(count / pageLimit), 100));
  const changePage = page => {
    const newSearchParams = new URLSearchParams(searchParams);

    newSearchParams.set("page", page);

    router.push(`?${newSearchParams.toString()}`, { scroll: device.isPhone });
  };

  return device.measured === false || count === 0 ? null : (
    <Stack height="100%" justifyContent="flex-end" alignItems="center">
      <Stack
        width="100%"
        direction="row"
        justifyContent={{ compact: "space-between", expanded: "center" }}
      >
        {device.isPhone ? (
          <>
            <ButtonText
              label="Previous"
              IconStart={PreviousIcon}
              disabled={page === 1}
              onClick={() => changePage(page - 1)}
            />
            <ButtonText
              label="Next"
              IconEnd={NextIcon}
              disabled={page === lastPage}
              onClick={() => changePage(page + 1)}
            />
          </>
        ) : (
          <Pagination
            page={page}
            boundaryCount={1}
            siblingCount={2}
            shape="rounded"
            count={lastPage}
            onChange={(_, page) => changePage(page)}
          />
        )}
      </Stack>
    </Stack>
  );
});

function Filters({ sort, task, categories }) {
  const [options] = useState([
    ["default", "default"],
    ["free", "free access"],
    ...modelSort,
    ["closed-source", "closed source"]
  ]);
  const { bottomSheet } = useStore();
  const searchParams = useSearchParams();
  const tasksSelected = task !== undefined;
  const paramsWithoutSort = new URLSearchParams(searchParams);

  paramsWithoutSort.delete("sort");
  paramsWithoutSort.delete("page");

  return (
    <Stack
      spacing={1}
      justifyContent="center"
      direction={{ compact: "row", expanded: "column" }}
    >
      <ButtonOutlined
        IconStart={({ sx }) => (
          <TaskIcon
            sx={{
              ...sx,
              height: 20,
              width: 20,
              mr: 1,
              color: tasksSelected
                ? "var(--secondary-on-container)"
                : "var(--surface-on-variant)"
            }}
          />
        )}
        label={`${tasksSelected ? "Edit" : "Add"} filter`}
        sx={{
          typography: "labelLg",
          width: "max-content",
          borderRadius: "var(--shape-sm)",
          color: tasksSelected
            ? "var(--secondary-on-container)"
            : "var(--surface-on-color)",
          borderColor: tasksSelected
            ? "var(--secondary-color)"
            : "var(--outline-variant)",
          bgcolor: tasksSelected
            ? "var(--secondary-container)"
            : "var(--surface-container-color)",
          display: { compact: "flex", expanded: "none" }
        }}
        onClick={() =>
          bottomSheet.configure(() => <Tasks categories={categories} />)
        }
      />
      <TextField
        select
        fullWidth
        name="sort"
        variant="standard"
        sx={{
          p: 0.5,
          border: 1,
          typography: "labelLg",
          borderRadius: "var(--shape-sm)",
          borderColor: "var(--outline-variant)",
          bgcolor: {
            compact: "var(--surface-container-color)",
            expanded: "unset"
          }
        }}
        value={sort}
        slotProps={{
          select: { IconComponent: () => null },
          input: {
            sx: { typography: "labelLg" },
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SortIcon
                  sx={{
                    ml: 1,
                    color: "var(--surface-variant-on)",
                    width: 20,
                    height: 20
                  }}
                />
              </InputAdornment>
            )
          }
        }}
      >
        {options.map(([value, label]) => (
          <MenuItem
            key={label}
            value={value}
            component={NextLink}
            href={`?${
              // if nothing selected, select sort
              paramsWithoutSort.size === 0
                ? `sort=${value}`
                : // if selected, remove sort
                  searchParams.get("sort") === value
                  ? paramsWithoutSort.toString()
                  : `${paramsWithoutSort.toString()}&sort=${value}`
            }`}
          >
            {`Sort by ${label}`}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}
